import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import * as api from "../lib/api";
import "./Savkabile.css";

interface Kabile {
  id: string;
  name: string;
  description: string;
  flagUrl?: string;
  leaderId: string;
  maxMembers: number;
  minTP: number;
  createdAt: Date;
  members: any[];
  totalTP: number;
}

export default function Savkabile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [hasKabile, setHasKabile] = useState(false);
  const [myKabile, setMyKabile] = useState<any>(null);
  const [kabileler, setKabileler] = useState<Kabile[]>([]);
  const [isLoadingKabileler, setIsLoadingKabileler] = useState(false);
  const [showKabileler, setShowKabileler] = useState(true);
  const [showSavaslar, setShowSavaslar] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    checkUserKabile();
    loadKabileler();
  }, []);

  const checkUserKabile = async () => {
    try {
      const kabile = await api.getMyKabile();
      setHasKabile(!!kabile);
      setMyKabile(kabile);
    } catch (error) {
      // User doesn't have a kabile
      setHasKabile(false);
      setMyKabile(null);
    } finally {
      setIsLoading(false);
    }
  };

  const loadKabileler = async () => {
    setIsLoadingKabileler(true);
    try {
      const allKabileler = await api.getAllKabileler();
      setKabileler(allKabileler);
    } catch (error) {
      console.error("Kabileler yüklenemedi:", error);
    } finally {
      setIsLoadingKabileler(false);
    }
  };

  const handleKabileAction = () => {
    if (hasKabile) {
      navigate("/savkabile/my");
    } else {
      navigate("/savkabile/create");
    }
  };

  const handleAdminDeleteKabile = async (kabileId: string, kabileName: string) => {
    if (!confirm(`⚠️ ADMIN YETKİSİ\n\n"${kabileName}" kabilesini silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz!`)) {
      return;
    }

    try {
      await api.deleteKabile(kabileId);
      alert("Kabile admin tarafından silindi");
      await loadKabileler(); // Refresh list
    } catch (error: any) {
      alert(error.message || "Kabile silinemedi");
    }
  };

  if (isLoading) {
    return (
      <div className="savkabile-container">
        <div className="savkabile-loading-spinner">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="savkabile-container">
      <div className="savkabile-content">
        <header className="savkabile-header">
          <div className="savkabile-hero-section">
            <h1 className="savkabile-hero-title">
              🏛️ <span className="savkabile-gradient-text">SavKabile</span>
            </h1>
            <p className="savkabile-hero-subtitle">
              Kabilenizi oluşturun veya bir kabileye katılarak güçlenin
            </p>
          </div>
        </header>

        <main className="savkabile-main-content">
          {!hasKabile && (
            <div className="create-kabile-section">
              <div className="no-kabile-notice">
                <span className="notice-icon">ℹ️</span>
                <p>Henüz bir kabileye üye değilsiniz. Kendi kabilenizi oluşturabilir veya aşağıdaki kabilelerden birine katılım talebi gönderebilirsiniz.</p>
              </div>
              <button onClick={() => navigate("/savkabile/create")} className="create-kabile-main-btn">
                ➕ Kabile Oluştur
              </button>
            </div>
          )}

          {hasKabile && myKabile && (
            <div className="my-kabile-section">
              <div className="my-kabile-badge">👑 Kabilen</div>
              <div className="my-kabile-info">
                {myKabile.flagUrl && (
                  <img src={myKabile.flagUrl} alt={myKabile.name} className="my-kabile-flag" />
                )}
                <div className="my-kabile-details">
                  <h3 className="my-kabile-name">{myKabile.name}</h3>
                  <p className="my-kabile-stats">
                    👥 {myKabile.memberCount} Üye • ⚔️ {myKabile.totalTP?.toLocaleString() || '0'} TP • 💎 {myKabile.totalKP?.toLocaleString() || '0'} KP
                  </p>
                </div>
              </div>
              <button onClick={() => navigate("/savkabile/my")} className="my-kabile-btn">
                Kabilemi Görüntüle →
              </button>
            </div>
          )}

          <section className="kabile-savaslar-section">
            <h2 
              className="section-title section-title-clickable" 
              onClick={() => setShowSavaslar(!showSavaslar)}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {showSavaslar ? '▼' : '▶'} ⚔️ Kabile Savaşları
            </h2>
            
            {showSavaslar && (
              <div className="savaslar-content">
                {hasKabile ? (
                  <>
                    <div className="battle-active-card">
                      <div className="battle-icon">⚔️</div>
                      <h3>Günlük Soru Savaşı</h3>
                      <p>Her gün yeni bir soru ile kabileler arası fikir savaşı!</p>
                      <button 
                        onClick={() => navigate("/savkabile/battle")} 
                        className="battle-enter-btn"
                      >
                        🎯 Savaşa Katıl
                      </button>
                      <div className="battle-info">
                        <p>✍️ Cevap yaz ve kabile arkadaşlarının cevaplarına oy ver</p>
                        <p>🏆 Liderin seçtiği cevap global arenada KP için yarışır</p>
                      </div>
                    </div>
                    
                    <div className="battle-active-card global-arena-card">
                      <div className="battle-icon">🌍</div>
                      <h3>Global Arena</h3>
                      <p>Tüm kabilelerin cevapları burada yarışıyor!</p>
                      <button 
                        onClick={() => navigate("/savkabile/global")} 
                        className="battle-enter-btn global-btn"
                      >
                        🌍 Global Arenaya Git
                      </button>
                      <div className="battle-info">
                        <p>❤️ Beğendiğin cevaplara KP ver</p>
                        <p>🏅 En çok KP alan kabileler kazanır!</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="battle-locked-card">
                    <div className="locked-icon">🔒</div>
                    <h3>Savaşlara Katılmak İçin</h3>
                    <p>Önce bir kabileye katılman gerekiyor!</p>
                    <button 
                      onClick={() => {
                        setShowKabileler(true);
                        setTimeout(() => {
                          document.querySelector('.kabileler-list-section')?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }} 
                      className="create-kabile-btn"
                    >
                      🏛️ Tüm Kabileler
                    </button>
                  </div>
                )}
              </div>
            )}
          </section>

          <section className="kabile-rankings-section">
            <button 
              onClick={() => navigate("/savkabile/rankings")} 
              className="kabile-rankings-btn"
            >
              🏆 Kabile Sıralamaları
            </button>
          </section>

          <section className="kabileler-list-section">
            <h2 
              className="section-title section-title-clickable" 
              onClick={() => setShowKabileler(!showKabileler)}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {showKabileler ? '▼' : '▶'} 🏛️ Tüm Kabileler
            </h2>
            
            {showKabileler && (
              <>
                <div className="kabile-search-box">
                  <input
                    type="text"
                    placeholder="🔍 Kabile adı ara..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="kabile-search-input"
                  />
                </div>
                
                {isLoadingKabileler ? (
                  <div className="loading-spinner">Kabileler yükleniyor...</div>
                ) : kabileler.length === 0 ? (
                  <div className="no-kabileler-message">
                    <p>Henüz hiç kabile oluşturulmamış</p>
                    <small>İlk kabileyi sen oluştur!</small>
                  </div>
                ) : (
                  <div className="kabileler-grid">
                    {kabileler
                      .filter((kabile) => 
                        kabile.name.toLowerCase().includes(searchQuery.toLowerCase())
                      )
                      .map((kabile) => (
                      <div 
                        key={kabile.id} 
                        className="kabile-card"
                      >
                        <div 
                          onClick={() => navigate(`/savkabile/${kabile.id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="kabile-card-header">
                            {kabile.flagUrl ? (
                              <img src={kabile.flagUrl} alt={kabile.name} className="kabile-card-flag" />
                            ) : (
                              <div className="kabile-card-flag-placeholder">🏛️</div>
                            )}
                            <div className="kabile-card-info">
                              <h3 className="kabile-card-name">{kabile.name}</h3>
                              <p className="kabile-card-description">
                                {kabile.description || "Açıklama yok"}
                              </p>
                            </div>
                          </div>
                          
                          <div className="kabile-card-stats">
                            <div className="kabile-stat">
                              <span className="stat-icon">👥</span>
                              <span className="stat-value">{kabile.members.length}/{kabile.maxMembers}</span>
                            </div>
                            <div className="kabile-stat">
                              <span className="stat-icon">⚔️</span>
                              <span className="stat-value">{kabile.totalTP.toLocaleString()} TP</span>
                            </div>
                            <div className="kabile-stat">
                              <span className="stat-icon">🎯</span>
                              <span className="stat-value">Min {kabile.minTP} TP</span>
                            </div>
                          </div>
                        </div>

                        {user?.isAdmin && (
                          <button
                            className="admin-delete-kabile-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAdminDeleteKabile(kabile.id, kabile.name);
                            }}
                          >
                            🗑️ Admin: Sil
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}